import React, { useState, useEffect, useMemo } from 'react';
import { CarouselViewByTrack } from './CarouselLayout/CarouselViewByTrack';
import { SessionLayout } from '../../../lib/api/custom-page.types';
import { CarouselViewByDay } from './CarouselLayout/CarouselViewByDay';
import GridLayout, { IGroupedMeetingsList, PrivateMeetingSessionList } from './GridLayout/index';
import { EventPrivateDetailsResponseType, MeetingDetailsType, PrivateMeetingSessionDetailsResponseType } from '../../../lib/api';
import { useSessionMeetingsList } from '../../../hooks/api/protected/use-private-session-meeting-list';
import { EventPrivateSettingsType } from '../../../lib/context-providers/event-settings-context/event-settings-context';
import { PassportNonce } from '../../../lib/constants';
import { DateFilter } from './DateFilter';
import TogglePastSessionsBtn from './GridLayout/TogglePastSessionsBtn';
import { useComposeMeeting } from './useComposeMeeting';
import { getGroupedMeetingsList } from '../../../lib/helpers/meetingHelper';
import useEventToken from '../../../hooks/use-event-token';
import { SearchInput } from '../../../components/search-input/search-input';
import { SearchResultsStatus } from '../../../components/search-results-status/search-results-status';
import { useFilteredGridSessions } from '../../../hooks/use-search-sessions';
import './LayoutWrapper.scss';
import { useTemplateProcessor } from '../../../hooks/use-template-processor';
import { getEventOffsetGMT, TimeZoneDisplayTypeLabels } from '../../../lib/helpers/dateHelper';
import { getDefaultUpcomingDateLabel } from '../../../lib/helpers/dateSelectorHelper';
import { utcToZonedTime } from 'date-fns-tz';
import SafeHTMLRender from '../../shared/SafeHTMLRender';
import { MediaQueryContextType, useMediaQuery } from '../../../lib/context-providers/media-query-provider';

export type EventType = EventPrivateDetailsResponseType & EventPrivateSettingsType;


export interface IMeetingsByStartTime {
  meetingsList: PrivateMeetingSessionList;
  startTime: string;
  endDateTime: Date;
}

interface IProps {
  event: EventType
}

export interface IHeaderProps {
  event: EventType;
  isGridLayout: boolean;
  isTogglePastMeetingsBtnShown: boolean;
  showPastMeetings: boolean;
  changeShowPastFlag: (showPastMeetings: boolean) => void;
  showAllDates: boolean;
  activeDates: string[];
  selectedDate: string;
  changeShowAllDates: () => void;
  onSelectedDateChange: (date: string) => void;
  handleSearch: (value: string) => void;
  filteredMeetingsLength: number;
  searchedValue: string;
  filteredMeetingsTracks: number;
  isMobile: boolean | void;
}

export interface ILayoutComponentProps {
  event: EventType;
  isGridLayout: boolean;
  meetings: IGroupedMeetingsList;
  eventToken: string;
  onSubscriptionChange?: (meetingId: string, subscribed: boolean) => void;
}
export interface IFilteredMeetings {
  filteredMeetingsLength: number;
}
export interface IGridFilteredMeetings extends IFilteredMeetings {
  tracksCount: Set<string>;
}

const HeaderComponent = (props: IHeaderProps): JSX.Element => {
  const {
    event,
    isGridLayout,
    isTogglePastMeetingsBtnShown,
    changeShowPastFlag,
    showPastMeetings,
    activeDates,
    showAllDates,
    selectedDate,
    changeShowAllDates,
    onSelectedDateChange,
    handleSearch,
    filteredMeetingsLength,
    searchedValue,
    filteredMeetingsTracks,
    isMobile,
  } = props;
  const className = 'editable-session';
  const classNameSessionHeader = `${className}--header`;
  const classNameSessionPast = `${className}--past-sessions`;
  const classNameSessionHeaderRight = `${className}--header-right`;
  const isPrimaryColor = event.primaryColor ? 'fill-primary text-primary' : 'tracks';
  const showTracksSection = filteredMeetingsTracks > 1;
  const datesWrapperClass = `${isMobile ? 'flex-wrap ' : ''} flex items-center justify-between grid-days-wrapper`;
  const togglePastSessionsClass = ` md:float-none whitespace-no-wrap w-44 ml-auto`;
  const trackWrapperClass = `flex items-center ${isMobile ? 'w-full ' : ''} ${isMobile && (isTogglePastMeetingsBtnShown || showTracksSection) ? ' mb-4' : ''}`;
  const shouldAddTopMargin = isGridLayout ? '' : 'm-top-30px';

  return (
    <>
      <div className={`${classNameSessionHeader} md:pr-0`}>
        <div className='w-full flex justify-between items-start md:flex-no-wrap flex-wrap'>
          <div className='pl-5 pr-5 flex justify-between md:w-9/12 w-full mb-4 md:mb-0'>
            <h2 className='font-size-40px font-normal leading-none mt-1'>{event.sessionLabel}</h2>
          </div>
          <SearchInput
            onChange={handleSearch}
            withDebounce
            withResetSearchButton
          />
        </div>
        {event.sessionNotes &&
          <>
            <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={event.sessionNotes} className={`pl-5 pr-5 m-top-28px word-break`}/>
            {(isGridLayout || searchedValue) && <hr className='m-top-30px'/>}
          </>
        }
        {/* TODO: delete after add filter to carousel layout */}
        {isGridLayout
          ? (
            <div className={`${datesWrapperClass} pl-5`}>
              <DateFilter
                dates={activeDates}
                timeZone={event.timeZone}
                showAllDates={showAllDates}
                selectedDate={selectedDate}
                changeShowAllDates={changeShowAllDates}
                onSelectedDateChange={onSelectedDateChange}
                isTogglePastMeetingsBtnShown={isTogglePastMeetingsBtnShown}
                showTracksSection={showTracksSection}
              />
              <div className={trackWrapperClass}>
                {
                  showTracksSection && (
                    <div
                      className={`${isPrimaryColor} font-size-12px`}
                    >
                      {filteredMeetingsTracks} tracks
                    </div>
                  )
                }
                {isTogglePastMeetingsBtnShown
                  ? (
                    <div
                      className={classNameSessionHeaderRight + togglePastSessionsClass}
                    >
                      <TogglePastSessionsBtn
                        className={classNameSessionPast}
                        changeShowPastFlag={changeShowPastFlag}
                        showPastMeetings={showPastMeetings}
                      />
                    </div>
                  )
                  : null
                }
              </div>
            </div>
          )
          : null
        }
      </div>
      {!!searchedValue &&
        <>
          {(!event.sessionNotes || isGridLayout) && <hr className={shouldAddTopMargin}/>}
          <SearchResultsStatus
            as='section'
            className='sessions-search-result pl-5 pr-5'
            count={filteredMeetingsLength}
            searchValue={searchedValue}
          />
          {isGridLayout && showAllDates && !filteredMeetingsLength && <hr className='m-top-20px'/>}
        </>
      }
      { !isGridLayout && <hr className='m-top-20px m-bottom-35px' /> }
    </>
  );
};

const SESSIONS_PAGE_STORAGE_KEY = 'sessionsPageParams';

const isMeetingFinished = (meeting: MeetingDetailsType): boolean => (new Date(meeting.endDateTime).getTime()) < (new Date()).getTime();
const isOngoingMeeting = (meeting: MeetingDetailsType): boolean => (new Date(meeting.endDateTime).getTime()) > (new Date()).getTime();

const LayoutComponent: React.FC<ILayoutComponentProps> = ({
  event,
  meetings,
  isGridLayout,
  eventToken,
  onSubscriptionChange
}: ILayoutComponentProps) => {
  const [showPastMeetings, setShowPastMeetings] = useState<Record<string, boolean>>({});
  const [isTogglePastMeetingsBtnShown, setIsTogglePastMeetingsBtnShown] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [filteredMeetingsLengthState, setfilteredMeetingsLengthState] = useState<number>(0);
  const [filteredMeetingsTracks, setFilteredMeetingsTracks] = useState<number>(0);
  const mediaQuery = useMediaQuery() as MediaQueryContextType;
  const isMobile = mediaQuery?.isMobile;
  const handleChangeShowPastMeetings = (label: string, newState: boolean): void => {
    setShowPastMeetings(prev => ({
      ...prev,
      [label]: newState
    }));
  };

  const getDefaultSelectedDate = (): string => {
    const days = Object.keys(meetings);
    const entries = days.map((day) => [day, meetings[day]] as [string, typeof meetings[string]]);
    return getDefaultUpcomingDateLabel(entries);
  };

  const [selectedDate, setMeetingsDate] = useState<string>(() => getDefaultSelectedDate());
  const [showAllDates, changeShowAllDates] = useState<boolean>(!isGridLayout);

  const onSelectedDateChange = (selectedDate: string) => {
    window.sessionStorage.setItem(SESSIONS_PAGE_STORAGE_KEY, JSON.stringify({
      selectedDate,
      showAllDates: false
    }));
    setMeetingsDate(selectedDate);
    if (showAllDates) {
      changeShowAllDates(false);
    }
  };

  const activeDates = Object.keys(meetings);
  const date = selectedDate || getDefaultSelectedDate();
  const meetingsListByDate = meetings[date];

  const allMeetings = Object.values(meetings).reduce((_meetings, meetingsList) => _meetings.concat(meetingsList), []);
  const now = (new Date()).getTime();
  const allMeetingsInPast = allMeetings.every(meeting => (new Date(meeting.endDateTime)).getTime() < now);


  const checkIfAllMeetingsInFuture = (meetings: MeetingDetailsType[]): boolean => {
    let _allMeetingsInFuture = false;

    const meetingsGroupedByStartDateTime = meetings.reduce((group, meeting) => {
      group[meeting.startDateTime] = (group[meeting.startDateTime] || []).concat(meeting);
      return group;
    }, {} as Record<string, MeetingDetailsType[]>);

    for (const meetingGroup of Object.values(meetingsGroupedByStartDateTime)) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (meetingGroup.length === 1) {
        if (isMeetingFinished(meetingGroup[0])) {
          return false;
        } else {
          _allMeetingsInFuture = true;
        }
      } else {
        _allMeetingsInFuture = meetingGroup.some(isOngoingMeeting);
      }
    }

    return _allMeetingsInFuture;
  };

  const allMeetingsInFuture = checkIfAllMeetingsInFuture(allMeetings);
  const activeDateLabel = showAllDates ? showPastMeetings['showAllDates'] : showPastMeetings[selectedDate];
  const showPastMeetingsFlag = event.pastSessionsEnabled && !!activeDateLabel;
  const { searchedMeetings } = useFilteredGridSessions(meetingsListByDate, searchedValue);

  const { activeMeetings, meetingsByStartTime } = useComposeMeeting(searchedMeetings, date, showPastMeetingsFlag);
  const { meetingsByStartTime: initialMeetingsByStartTime } = useComposeMeeting(meetingsListByDate, date, showPastMeetingsFlag);
  const startDateTime = utcToZonedTime(event.startTime, event.timeZone);
  const templateProcessor = useTemplateProcessor();
  const timeZoneLabel = getEventOffsetGMT(
    event.timeZoneDisplayType as TimeZoneDisplayTypeLabels,
    event.customTimeZoneDisplay as string,
    startDateTime,
    event.timeZone
  );
  const getTransformedTimeZone = () => {
    return event.timeZoneDisplayType === TimeZoneDisplayTypeLabels.Custom ? templateProcessor(timeZoneLabel) : timeZoneLabel;
  };
  const transformedTimeZone = event.showEventTimeZone ? getTransformedTimeZone() : '';

  const handleChangeShowAllDates = () => {
    const newToggleState = !showAllDates;
    changeShowAllDates(newToggleState);
    window.sessionStorage.setItem(SESSIONS_PAGE_STORAGE_KEY, JSON.stringify({
      selectedDate,
      showAllDates: newToggleState
    }));
  };


  const Layout = {
    [SessionLayout.GRID_VIEW_BY_TRACK]: () => (
      <GridLayout
        event={event}
        meetings={meetings}
        showAllDates={showAllDates}
        selectedDate={selectedDate}
        activeMeetings={activeMeetings}
        showPastMeetings={showPastMeetingsFlag}
        meetingsByStartTime={meetingsByStartTime}
        eventToken={eventToken}
        searchedValue={searchedValue}
        setFilteredMeetingsCount={data => {
          setfilteredMeetingsLengthState(data.filteredMeetingsLength);
          setFilteredMeetingsTracks(data.tracksCount.size);
        }}
        transformedTimeZone={transformedTimeZone}
        isMobile={isMobile}
        onSubscriptionChange={onSubscriptionChange}
      />
    ),
    [SessionLayout.CAROUSEL_VIEW_BY_TRACK]: () => (
      <CarouselViewByTrack
        event={event}
        meetings={meetings}
        showAllDates={true}
        selectedDate={selectedDate}
        showPastMeetings={showPastMeetingsFlag}
        searchedValue={searchedValue}
        setFilteredMeetingsCount={data => {
          setfilteredMeetingsLengthState(data.filteredMeetingsLength);
        }}
        eventToken={eventToken}
        transformedTimeZone={transformedTimeZone}
        onSubscriptionChange={onSubscriptionChange}
      />
    ),
    [SessionLayout.CAROUSEL_VIEW_BY_DAY]: () => (
      <CarouselViewByDay
        event={event}
        meetings={meetings}
        showAllDates={true}
        selectedDate={selectedDate}
        showPastMeetings={showPastMeetingsFlag}
        searchedValue={searchedValue}
        setFilteredMeetingsCount={data => {
          setfilteredMeetingsLengthState(data.filteredMeetingsLength);
        }}
        eventToken={eventToken}
        transformedTimeZone={transformedTimeZone}
        onSubscriptionChange={onSubscriptionChange}
      />
    )
  }[event?.layout];

  useEffect(() => {
    const sessionsPageParams = window.sessionStorage.getItem(SESSIONS_PAGE_STORAGE_KEY);
    if (sessionsPageParams) {
      const { selectedDate, showAllDates } = JSON.parse(sessionsPageParams);
      changeShowAllDates(showAllDates);
      if (meetings[selectedDate]) {
        setMeetingsDate(selectedDate);
      } else {
        setMeetingsDate(getDefaultSelectedDate());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings]);

  useEffect(() => {
    const nowDateInMs = new Date().getTime();
    const endedMeetings = initialMeetingsByStartTime.filter(m => m.endDateTime.getTime() > nowDateInMs);
    if (showAllDates) {
      if (!isGridLayout) {
        handleChangeShowPastMeetings('showAllDates', true);
        return;
      }
      if (allMeetingsInPast || allMeetingsInFuture) {
        handleChangeShowPastMeetings('showAllDates', true);
        return;
      }
      setIsTogglePastMeetingsBtnShown(event.pastSessionsEnabled);
      const showPastMeetingsState = showPastMeetings['showAllDates'] === undefined ? false : showPastMeetings['showAllDates'];
      handleChangeShowPastMeetings('showAllDates', showPastMeetingsState);

    } else {
      const isBtnShown = event.pastSessionsEnabled ? endedMeetings.length > 0 && endedMeetings.length !== initialMeetingsByStartTime.length : false;
      setIsTogglePastMeetingsBtnShown(isBtnShown);
      const showPastMeetingsState = showPastMeetings[selectedDate] === undefined ? !isBtnShown : showPastMeetings[selectedDate];
      handleChangeShowPastMeetings(selectedDate, showPastMeetingsState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMeetingsInPast, meetingsByStartTime, showAllDates]);

  return (
    <>
      <HeaderComponent
        event={event}
        activeDates={activeDates}
        showAllDates={showAllDates}
        selectedDate={selectedDate}
        isGridLayout={isGridLayout}
        showPastMeetings={showPastMeetingsFlag}
        changeShowPastFlag={newState => handleChangeShowPastMeetings(showAllDates ? 'showAllDates' : selectedDate, newState)}
        onSelectedDateChange={onSelectedDateChange}
        changeShowAllDates={() => handleChangeShowAllDates()}
        isTogglePastMeetingsBtnShown={isTogglePastMeetingsBtnShown}
        handleSearch={setSearchedValue}
        filteredMeetingsLength={filteredMeetingsLengthState}
        searchedValue={searchedValue}
        filteredMeetingsTracks={filteredMeetingsTracks}
        isMobile={isMobile}
      />
      <Layout />
    </>
  );
};

const LayoutWrapper: React.FC<IProps> = ({ event }: IProps) => {
  const isGridLayout = SessionLayout.GRID_VIEW_BY_TRACK === event?.layout;
  const isCarouselViewByDay = SessionLayout.CAROUSEL_VIEW_BY_DAY === event?.layout;

  const { data } = useSessionMeetingsList(String(event.eventId)) as { data?: PrivateMeetingSessionList };
  const eventToken = useEventToken(event?.eventId);
  const className = 'editable-session';
  const classNameSessionWrapper = `${className}--wrapper`;

  const meetings = useMemo(() => {
    if (!data) return;

    const groupedMeetings = getGroupedMeetingsList<PrivateMeetingSessionDetailsResponseType>(data, isCarouselViewByDay, event.timeZone);

    return groupedMeetings;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSubscriptionChange = (meetingId: string, subscribed: boolean): void => {
    if (!meetings) return;
    Object.keys(meetings).forEach(dateKey => {
      meetings[dateKey] = meetings[dateKey].map(meeting =>
        meeting.meetingId === meetingId
          ? { ...meeting, attendeeSubscribedToMeeting: subscribed }
          : meeting
      );
    });
  };

  if (!meetings) return null;

  const isDataExist = (meetings && !!Object.keys(meetings).length && true);

  return (
    <div className={`${classNameSessionWrapper} shadow-gray bg-white mx-auto container md:w-3/4 md:px-10 pb-8 mt-12 max-h-full h-70 w-100 mx-0 px-0`}>
      {
        isDataExist
          ? (
            <LayoutComponent
              event={event}
              isGridLayout={isGridLayout}
              meetings={meetings}
              eventToken={eventToken}
              onSubscriptionChange={handleSubscriptionChange}
            />
          )
          : (
            <>
              <div className='font-size-40px pt-2 md:pb-8 pl-4 md:flex justify-between items-end leading-none'>
                {event.sessionLabel}
              </div>
              {event.sessionNotes &&
                <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={event.sessionNotes} className='py-4 sm:py-4 md:pt-0 px-4 break-words'/>
              }
              <hr className='mt-4' />
            </>
          )
      }
    </div>
  );
};

export { LayoutWrapper, LayoutComponent, HeaderComponent };
